import 'react-intl-tel-input/dist/main.css';
import './styles.css';

import { forwardRef, useRef } from 'react';

import IntlTelInput from 'react-intl-tel-input';
import { getEventDetails } from '@store/slices/eventSlice';
import { useSelector } from 'react-redux';

interface WePhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  className?: string;
  borderColor?: string;
  focusBorderColor?: string;
}

const WePhoneInput = forwardRef(
  (
    {
      value,
      onChange,
      className,
      borderColor,
      focusBorderColor,
    }: WePhoneInputProps,
    ref
  ) => {
    const telInputRef = useRef<HTMLInputElement | null>(null);
    const sluggedEvent = useSelector(getEventDetails)?.[0];

    const setCursorToEnd = () => {
      const input = telInputRef.current;
      if (input) {
        input.focus();
        requestAnimationFrame(() => {
          const length = input.value.length;
          input.setSelectionRange(length, length);
        });
      }
    };

    const sanitizePhone = (value: string) => {
      if (!value) return value;

      // Keep leading +, remove everything else that isn't a digit
      const hasPlus = value.startsWith('+');
      const digitsOnly = value.replace(/\D/g, '');

      return hasPlus ? `+${digitsOnly}` : digitsOnly;
    };

    return (
      <div
        className={className}
        style={
          {
            '--phone-border-color': borderColor || '#EDEDED',
            '--phone-focus-border-color': focusBorderColor || '#009FE3',
          } as React.CSSProperties
        }
      >
        <IntlTelInput
          preferredCountries={[sluggedEvent?.language === 'de' ? 'de' : 'us']}
          value={value}
          onPhoneNumberChange={(isValid, num, countryData, fullNum) => {
            const sanitized = sanitizePhone(fullNum);
            onChange(sanitized);

            setCursorToEnd();
          }}
          ref={input => {
            telInputRef.current = input?.tel as HTMLInputElement | null;
          }}
          fieldId="phone"
        />
      </div>
    );
  }
);

WePhoneInput.displayName = 'WePhoneInput';

export default WePhoneInput;
